<script setup>
  import { ref, computed, watch } from 'vue';

  const emit = defineEmits([
    'email-checked',
    'show-login',
    'show-password-reset',
    'guest-data-updated',
  ]);

  const email = ref('');
  const firstName = ref('');
  const lastName = ref('');
  const wantsAccount = ref(false);
  const emailStatus = ref(null);
  const emailError = ref('');
  const isLoading = ref(false);
  const checkTimeout = ref(null);

  const showGuestOptions = computed(() => {
    return emailStatus.value && emailStatus.value.available && email.value;
  });
  const isValidEmail = (emailValue) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };

  const onEmailInput = () => {
    emailError.value = '';
    emailStatus.value = null;

    // Clear existing timeout
    if (checkTimeout.value) {
      clearTimeout(checkTimeout.value);
    }

    // Set new timeout for delayed checking
    checkTimeout.value = setTimeout(() => {
      if (email.value && isValidEmail(email.value)) {
        checkEmail();
      }
    }, 500);
  };

  const checkEmail = async () => {
    if (!email.value) {
      emailStatus.value = null;
      return;
    }

    if (!isValidEmail(email.value)) {
      emailError.value = 'Invalid email format';
      emailStatus.value = null;
      return;
    }

    isLoading.value = true;
    emailError.value = '';

    try {
      const response = await fetch('/api/guest/check-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.value }),
      });

      const data = await response.json();

      if (response.ok) {
        emailStatus.value = data;
        emit('email-checked', {
          email: email.value,
          status: data,
          firstName: firstName.value,
          lastName: lastName.value,
          wantsAccount: wantsAccount.value,
        });
      } else {
        emailError.value = data.error || 'Email check failed';
      }
    } catch (error) {
      console.error('Email check failed:', error);
      emailError.value = 'Email check failed';
    } finally {
      isLoading.value = false;
    }
  };
  const showLogin = () => {
    // Clear any pending email checks to prevent interference
    if (checkTimeout.value) {
      clearTimeout(checkTimeout.value);
      checkTimeout.value = null;
    }
    emit('show-login', { email: email.value });
  };

  const showPasswordReset = () => {
    // Clear any pending email checks to prevent interference
    if (checkTimeout.value) {
      clearTimeout(checkTimeout.value);
      checkTimeout.value = null;
    }
    emit('show-password-reset', { email: email.value });
  };

  const updateGuestData = () => {
    if (showGuestOptions.value) {
      emit('guest-data-updated', {
        email: email.value,
        firstName: firstName.value,
        lastName: lastName.value,
        wantsAccount: wantsAccount.value,
      });
    }
  };

  const continueAsGuest = () => {
    console.log('continueAsGuest clicked', { emailStatus: emailStatus.value });

    // Clear any pending email checks
    if (checkTimeout.value) {
      clearTimeout(checkTimeout.value);
      checkTimeout.value = null;
    }

    // For existing guests, get the name from the API response
    // The API now returns firstName and lastName for existing users
    const guestData = {
      email: email.value,
      firstName: emailStatus.value.firstName || firstName.value || '',
      lastName: emailStatus.value.lastName || lastName.value || '',
      wantsAccount: wantsAccount.value, // Use actual checkbox value for account upgrade
    };

    console.log('EmailStatus data:', emailStatus.value);
    console.log('Emitting guest-data-updated', guestData);
    emit('guest-data-updated', guestData);
  };

  // Watchers
  watch(email, onEmailInput);
  watch([firstName, lastName, wantsAccount], updateGuestData);
</script>

<template>
  <div class="email-check">
    <div class="mb-4">
      <label for="email" class="mb-1 block text-sm font-medium text-gray-700">
        {{ $t('checkout.email_address') }}
      </label>
      <input
        id="email"
        v-model="email"
        type="email"
        class="input"
        :class="{
          'border-red-500 focus:border-red-500 focus:ring-red-500': emailError,
          'border-green-500 focus:border-green-500 focus:ring-green-500':
            emailStatus && emailStatus.available,
        }"
        :placeholder="$t('checkout.email_placeholder')"
        required
        @input="onEmailInput"
      />
      <div v-if="emailError" class="mt-1 text-sm text-red-600">
        {{ emailError }}
      </div>
      <div v-if="isLoading" class="mt-1 text-sm text-gray-500">
        <i class="fas fa-spinner fa-spin mr-1"></i>
        {{ $t('checkout.checking_email') }}
      </div>
    </div>

    <!-- Email Status Display -->
    <div v-if="emailStatus && !isLoading" class="email-status mt-3">
      <!-- Available Email -->
      <div
        v-if="emailStatus.available"
        class="flex items-center rounded-md border border-green-200 bg-green-50 p-3 text-green-800"
      >
        <i class="fas fa-check-circle mr-2"></i>
        <span>{{ $t('checkout.email_available') }}</span>
      </div>

      <!-- Guest User Exists -->
      <div
        v-else-if="emailStatus.user_type === 'guest'"
        class="border-primary-200 bg-primary-50 text-primary-800 rounded-md border p-3"
      >
        <div class="mb-3 flex items-center">
          <i class="fas fa-user-clock mr-2"></i>
          <div>
            <strong>{{ $t('checkout.welcome_back_guest') }}</strong>
            <p class="mb-0">
              {{ $t('checkout.continue_with_existing_account') }}
            </p>
          </div>
        </div>

        <!-- Account upgrade option for existing guests -->
        <div class="mb-3 flex items-center">
          <input
            id="upgrade-account"
            v-model="wantsAccount"
            class="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
            type="checkbox"
          />
          <label class="ml-2 text-sm text-gray-700" for="upgrade-account">
            {{ $t('checkout.upgrade_to_full_account') }}
          </label>
        </div>

        <div class="flex justify-end">
          <button
            type="button"
            class="bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 inline-flex items-center rounded-md border border-transparent px-3 py-1 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
            @click.stop="continueAsGuest"
          >
            {{ $t('checkout.continue_as_guest') }}
          </button>
        </div>
      </div>

      <!-- Limited User Exists -->
      <div
        v-else-if="emailStatus.user_type === 'limited'"
        class="flex items-start rounded-md border border-yellow-200 bg-yellow-50 p-3 text-yellow-800"
      >
        <i class="fas fa-user mr-2 mt-1"></i>
        <div class="flex-1">
          <strong>{{ $t('checkout.account_exists') }}</strong>
          <p class="mb-2">{{ $t('checkout.limited_account_message') }}</p>
          <div class="flex space-x-2">
            <button
              type="button"
              class="bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 inline-flex items-center rounded-md border border-transparent px-3 py-1 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
              @click.stop="showLogin"
            >
              {{ $t('auth.sign_in') }}
            </button>
            <button
              type="button"
              class="border-primary-300 text-primary-700 hover:bg-primary-50 focus:ring-primary-500 inline-flex items-center rounded-md border bg-white px-3 py-1 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
              @click.stop="showPasswordReset"
            >
              {{ $t('auth.forgot_password') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Full User Exists -->
      <div
        v-else-if="emailStatus.user_type === 'full'"
        class="flex items-start rounded-md border border-yellow-200 bg-yellow-50 p-3 text-yellow-800"
      >
        <i class="fas fa-user-check mr-2 mt-1"></i>
        <div class="flex-1">
          <strong>{{ $t('checkout.account_exists') }}</strong>
          <p class="mb-2">{{ $t('checkout.full_account_message') }}</p>
          <div class="flex space-x-2">
            <button
              type="button"
              class="bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 inline-flex items-center rounded-md border border-transparent px-3 py-1 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
              @click.stop="showLogin"
            >
              {{ $t('auth.sign_in') }}
            </button>
            <button
              type="button"
              class="border-primary-300 text-primary-700 hover:bg-primary-50 focus:ring-primary-500 inline-flex items-center rounded-md border bg-white px-3 py-1 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
              @click.stop="showPasswordReset"
            >
              {{ $t('auth.forgot_password') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Guest User Options -->
    <div v-if="showGuestOptions" class="guest-options mt-4">
      <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h6 class="mb-3 text-base font-medium text-gray-900">
          {{ $t('checkout.guest_checkout_options') }}
        </h6>
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <input
              v-model="firstName"
              type="text"
              class="input"
              :placeholder="$t('user.first_name')"
            />
          </div>
          <div>
            <input
              v-model="lastName"
              type="text"
              class="input"
              :placeholder="$t('user.last_name')"
            />
          </div>
        </div>
        <div class="mt-3 flex items-center">
          <input
            id="create-account"
            v-model="wantsAccount"
            class="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
            type="checkbox"
          />
          <label class="ml-2 text-sm text-gray-700" for="create-account">
            {{ $t('checkout.create_account_for_future') }}
          </label>
        </div>
      </div>
    </div>
  </div>
</template>
